import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Dashboard.css";
import "../styles/Dispute.css";
import Breadcrumb from "./Breadcrumb";
import { useMatter } from "../context/MatterContext";
import { useModules } from "../context/ModulesContext";
import { fetchSurveyStatus, startSurveySession } from "../helpers/apiHelper";
import FullscreenLoader from "./FullscreenLoader";
import { Plus, Minus } from "lucide-react";
import {
  findDiscontinuanceModule,
} from "../modules/moduleUtils";

const getSurveyStatusStorageKey = (matterId) =>
  `bhp_dispute_survey_status_${matterId || "unknown"}`;

const readStoredSurveyStatus = (matterId) => {
  try {
    const raw = sessionStorage.getItem(getSurveyStatusStorageKey(matterId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeStoredSurveyStatus = (matterId, value) => {
  try {
    if (!matterId || !value) return;
    sessionStorage.setItem(
      getSurveyStatusStorageKey(matterId),
      JSON.stringify(value),
    );
  } catch {}
};

const clearStoredSurveyStatus = (matterId) => {
  try {
    if (!matterId) return;
    sessionStorage.removeItem(getSurveyStatusStorageKey(matterId));
  } catch {}
};

const resolveInvitationId = (locationState, currentSurveyStatus) => {
  return locationState?.invitationId || currentSurveyStatus?.invitationId || "";
};

const resolveDisputeStage = (invitationId, surveyStatus) => {
  const hasInvitation = Boolean(invitationId || surveyStatus?.invitationId);

  if (!hasInvitation) {
    return "not-started";
  }

  if (surveyStatus?.questionnaireCompleted) {
    return "submitted";
  }

  if (surveyStatus?.invitationStatus === "CLOSED") {
    return "submitted";
  }

  if (
    surveyStatus?.hasQuestionnaire &&
    surveyStatus?.invitationStatus === "OPENED"
  ) {
    return "in-progress";
  }

  return "in-progress";
};

export default function Dispute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { matter, party } = useMatter();
  const { modules } = useModules();

  const partyId = party?.id || party?.partyId || "";
  const matterId = matter?.id || matter?.matterId || "";
  const matterLabel = matter?.name || "Meu Caso";
  const discontinuanceModule = useMemo(
    () => findDiscontinuanceModule(modules, matterId),
    [modules, matterId],
  );
  const isDisputeEnrolled = Boolean(discontinuanceModule && matterId);

  const [startingDispute, setStartingDispute] = useState(false);
  const [loadingSurveyStatus, setLoadingSurveyStatus] = useState(false);
  const [surveyStatus, setSurveyStatus] = useState(null);

  const compensation = party?.compensation;

  const compensationDetails = useMemo(() => {
    if (!compensation) return null;

    if (compensation.pid) {
      return {
        type: "PID",
        ...compensation.pid,
      };
    }

    if (compensation.ff) {
      return {
        type: "FF",
        ...compensation.ff,
      };
    }

    if (compensation.novel) {
      return {
        type: "Novel",
        ...compensation.novel,
      };
    }

    return null;
  }, [compensation]);

  const hasReceivedCompensation = useMemo(() => {
    if (!compensationDetails) return false;

    return Boolean(
      compensationDetails.agreementDate ||
      compensationDetails.paymentDate ||
      compensationDetails.value != null,
    );
  }, [compensationDetails]);

  const disputeFaqs = useMemo(
    () => [
      {
        id: "dispute-faq-1",
        q: "Por que meu caso está em processo de encerramento?",
        a: "Seu caso pode entrar em processo de encerramento quando identificamos informações que indiquem recebimento de compensação anterior ou outro motivo que impeça a continuidade normal da ação.",
      },
      {
        id: "dispute-faq-2",
        q: "Posso contestar essa decisão?",
        a: "Sim. Caso você não concorde com a decisão, poderá enviar uma disputa por meio do portal. Nossa equipe analisará as informações enviadas antes de confirmar qualquer encerramento definitivo.",
      },
      {
        id: "dispute-faq-3",
        q: "O que acontece depois que eu envio a disputa?",
        a: "Após o envio do formulário, sua disputa será registrada e encaminhada para análise. Enquanto a revisão estiver em andamento, o fluxo automático de encerramento do caso ficará suspenso.",
      },
      {
        id: "dispute-faq-4",
        q: "Se eu fechar o formulário antes de terminar, perco o progresso?",
        a: "Não necessariamente. Caso o preenchimento já tenha sido iniciado, será possível retornar posteriormente para concluir o envio.",
      },
      {
        id: "dispute-faq-5",
        q: "O que significa quando a disputa aparece como “em análise”?",
        a: "Significa que o formulário foi enviado com sucesso e que nossa equipe está avaliando as informações apresentadas. Durante esse período, você deverá aguardar novas atualizações sobre o seu caso.",
      },
    ],
    [],
  );

  const invitationId = useMemo(() => {
    return resolveInvitationId(location.state, surveyStatus);
  }, [location.state, surveyStatus]);

  useEffect(() => {
    if (!matterId) {
      setSurveyStatus(null);
      return;
    }

    const stateSurveyStatus = location.state?.surveyStatus || null;
    const stateInvitationId = location.state?.invitationId || "";

    if (stateSurveyStatus) {
      setSurveyStatus(stateSurveyStatus);
      writeStoredSurveyStatus(matterId, stateSurveyStatus);
      return;
    }

    const stored = readStoredSurveyStatus(matterId);
    if (stored) {
      setSurveyStatus(stored);
      return;
    }

    if (!stateInvitationId) {
      setSurveyStatus(null);
    }
  }, [
    matterId,
    location.state?.surveyStatus,
    location.state?.invitationId,
  ]);

  useEffect(() => {
    if (isDisputeEnrolled || !matterId) return;
    clearStoredSurveyStatus(matterId);
    setSurveyStatus(null);
  }, [isDisputeEnrolled, matterId]);

  useEffect(() => {
    let cancelled = false;

    const loadSurveyStatus = async () => {
      const token = localStorage.getItem("accessToken");

      if (!token || !invitationId) {
        if (!cancelled) {
          setLoadingSurveyStatus(false);
        }
        return;
      }

      try {
        if (!cancelled) {
          setLoadingSurveyStatus(true);
        }

        const response = await fetchSurveyStatus(token, invitationId);

        if (cancelled) return;

        if (response) {
          setSurveyStatus(response);
          writeStoredSurveyStatus(matterId, response);
        }
      } catch {
        if (cancelled) return;
      } finally {
        if (!cancelled) {
          setLoadingSurveyStatus(false);
        }
      }
    };

    if (invitationId) {
      loadSurveyStatus();
    }

    return () => {
      cancelled = true;
    };
  }, [invitationId, matterId, location.state?.refreshedAt]);

  const disputeStage = useMemo(() => {
    return resolveDisputeStage(invitationId, surveyStatus);
  }, [invitationId, surveyStatus]);

  const stageContent = useMemo(() => {
    if (disputeStage === "submitted") {
      return {
        title: "Sua disputa está em análise",
        pill: "Em análise",
        intro:
          "Recebemos sua disputa com sucesso. No momento, ela está em análise pela nossa equipe. Aguarde novas atualizações sobre o seu caso.",
        actionLabel: "",
      };
    }

    if (disputeStage === "in-progress") {
      return {
        title: "Sua disputa ainda não foi concluída",
        pill: "Pendente",
        intro:
          "Identificamos que você já iniciou sua disputa, mas o envio ainda não foi concluído. Para que a análise comece, é necessário finalizar o formulário.",
        actionLabel: "Continuar disputa",
      };
    }

    return {
      title: "Seu caso está pendente de encerramento",
      pill: "Atenção",
      intro:
        "Identificamos informações que podem resultar na descontinuação do seu caso. Antes do encerramento definitivo, você pode revisar os detalhes e, caso discorde da decisão, enviar uma disputa para análise da nossa equipe.",
      actionLabel: "Iniciar disputa",
    };
  }, [disputeStage]);

  const formatDate = (value) => {
    if (!value) return "Não informado";

    const [year, month, day] = value.split("-");
    if (!year || !month || !day) return value;

    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (value) => {
    if (value == null) return "Não informado";

    return Number(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const handleStartDispute = async () => {
    if (startingDispute || disputeStage === "submitted" || !isDisputeEnrolled) return;

    const token = localStorage.getItem("accessToken");

    if (!token || !partyId || !matterId) {
      navigate(
        `/error?message=${encodeURIComponent(
          "Não foi possível iniciar a contestação.",
        )}`,
      );
      return;
    }

    try {
      setStartingDispute(true);

      const session = await startSurveySession(token, {
        partyId,
        matterId,
      });

      if (!session?.invitationLink || !session?.invitationId) {
        throw new Error("Survey session was not created correctly");
      }

      navigate("/surveys", {
        state: {
          url: session.invitationLink,
          invitationId: session.invitationId,
          caseId: session.caseId,
          questionnaireId: session.questionnaireId,
          token,
          matterId,
          partyId,
        },
      });
    } catch (error) {
      navigate(
        `/error?message=${encodeURIComponent(
          error.message || "Não foi possível iniciar a contestação.",
        )}`,
      );
    } finally {
      setStartingDispute(false);
    }
  };

  const renderStatusCard = () => {
    if (!isDisputeEnrolled) {
      return (
        <section className="surfaceDarkCard" aria-label="Status da contestação">
          <div className="sectionHeader sectionHeaderDark">
            <div className="sectionTitleDark">Disputa indisponível</div>
            <span className="pill pillWarn">Indisponível</span>
          </div>

          <div className="disputeIntroText">
            No momento, este caso não está inscrito no fluxo de contestação.
          </div>
        </section>
      );
    }

    if (loadingSurveyStatus) {
      return (
        <section className="surfaceDarkCard" aria-label="Status da contestação">
          <div className="sectionHeader sectionHeaderDark">
            <div className="sectionTitleDark">Verificando status</div>
            <span className="pill pillWarn">Aguarde</span>
          </div>

          <div className="disputeIntroText">
            Estamos consultando as informações mais recentes da sua disputa.
          </div>
        </section>
      );
    }

    return (
      <section className="surfaceDarkCard" aria-label="Status da contestação">
        <div className="sectionHeader sectionHeaderDark">
          <div className="sectionTitleDark">{stageContent.title}</div>
          <span className="pill pillWarn">{stageContent.pill}</span>
        </div>

        <div className="disputeIntroText">{stageContent.intro}</div>
      </section>
    );
  };

  const renderWhatHappensSection = () => {
    if (!isDisputeEnrolled) {
      return null;
    }

    if (loadingSurveyStatus || disputeStage === "submitted") {
      return null;
    }

    if (disputeStage === "in-progress") {
      return (
        <section
          className="surfaceDarkCard"
          aria-label="Próximo passo da contestação"
        >
          <div className="sectionHeader sectionHeaderDark">
            <div className="sectionTitleDark">Próximo passo</div>
          </div>

          <div className="disputeList">
            <div className="disputeListItem">
              <div className="disputeListBullet">•</div>
              <div className="disputeListText">
                Você já iniciou sua disputa anteriormente.
              </div>
            </div>

            <div className="disputeListItem">
              <div className="disputeListBullet">•</div>
              <div className="disputeListText">
                O formulário ainda não foi enviado, então a análise não começou.
              </div>
            </div>

            <div className="disputeListItem">
              <div className="disputeListBullet">•</div>
              <div className="disputeListText">
                Para seguir com a revisão do seu caso, é necessário concluir o
                preenchimento e enviar a disputa.
              </div>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        className="surfaceDarkCard"
        aria-label="O que acontece se você contestar"
      >
        <div className="sectionHeader sectionHeaderDark">
          <div className="sectionTitleDark">
            O que acontece ao iniciar uma disputa
          </div>
        </div>

        <div className="disputeList">
          <div className="disputeListItem">
            <div className="disputeListBullet">•</div>
            <div className="disputeListText">
              Você será direcionado para um formulário de disputa dentro do
              portal.
            </div>
          </div>

          <div className="disputeListItem">
            <div className="disputeListBullet">•</div>
            <div className="disputeListText">
              Nesse formulário, você poderá explicar por que discorda da decisão
              e anexar evidências que ajudem na análise.
            </div>
          </div>

          <div className="disputeListItem">
            <div className="disputeListBullet">•</div>
            <div className="disputeListText">
              Após o envio, sua disputa será registrada e encaminhada para
              revisão.
            </div>
          </div>

          <div className="disputeListItem">
            <div className="disputeListBullet">•</div>
            <div className="disputeListText">
              Quando uma disputa é aberta, o fluxo automático de encerramento do
              caso é interrompido enquanto a análise estiver em andamento.
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderBeforeContinueSection = () => {
    if (!isDisputeEnrolled) {
      return null;
    }

    if (loadingSurveyStatus || disputeStage === "submitted") {
      return null;
    }

    return (
      <section className="surfaceLight" aria-label="Antes de continuar">
        <div className="sectionHeader">
          <div className="sectionTitle">
            {disputeStage === "in-progress"
              ? "Continue sua disputa"
              : "Antes de continuar"}
          </div>
        </div>

        <div className="disputeInfoCard">
          {disputeStage === "in-progress" ? (
            <>
              <div className="disputeInfoText">
                Sua disputa já foi iniciada, mas ainda não foi enviada.
              </div>

              <div className="disputeInfoText">
                Ao continuar, você poderá retomar o preenchimento e concluir o
                envio do formulário.
              </div>
            </>
          ) : (
            <>
              <div className="disputeInfoText">
                Recomendamos que você tenha em mãos todas as informações e
                documentos que possam apoiar a sua disputa.
              </div>

              <div className="disputeInfoText">
                Ao clicar no botão abaixo, você seguirá para o formulário de
                disputa.
              </div>
            </>
          )}
        </div>

        <div className="disputeActionRow">
          <button
            type="button"
            className="disputePrimaryBtn"
            onClick={handleStartDispute}
            disabled={startingDispute}
          >
            {stageContent.actionLabel}
          </button>
        </div>
      </section>
    );
  };

  const renderFaqSection = () => {
    return (
      <section className="surfaceDarkCard" aria-label="Perguntas frequentes">
        <div className="sectionHeader sectionHeaderDark">
          <div className="sectionTitleDark">Perguntas frequentes</div>
        </div>

        <div className="disputeFaqList">
          {disputeFaqs.map((item) => (
            <details key={item.id} className="disputeFaqItem">
              <summary className="disputeFaqSummary">
                <span className="disputeFaqIconWrap" aria-hidden="true">
                  <Plus
                    className="disputeFaqIcon disputeFaqIconPlus"
                    size={18}
                  />
                  <Minus
                    className="disputeFaqIcon disputeFaqIconMinus"
                    size={18}
                  />
                </span>

                <span className="disputeFaqQuestion">{item.q}</span>
              </summary>

              <div className="disputeFaqAnswer">
                {String(item.a)
                  .split("\n")
                  .filter((line) => line !== undefined)
                  .map((line, idx) => (
                    <p key={idx} className="disputeFaqAnswerP">
                      {line.trim()}
                    </p>
                  ))}
              </div>
            </details>
          ))}
        </div>
      </section>
    );
  };

  return (
    <>
      {startingDispute && (
        <FullscreenLoader
          title="Aguarde enquanto preparamos seu formulário"
          message="Este processo pode levar alguns minutos."
        />
      )}

      <div className="main-wrapper--dashboard">
        <Breadcrumb
          items={[
            { label: "Página Inicial", link: "/dashboard" },
            { label: "Disputa" },
          ]}
        />

        <div className="dashPage">
          <div className="dashTop">
            <div className="dashHero">
              <div className="dashHello">Disputa</div>

              <div className="dashCaseLine">
                <span>{matterLabel}</span>
                <span className="dashCaseDivider">•</span>
                <span>Encerramento do caso</span>
              </div>

              <div className="dashHeroLine">
                Seu caso está em processo de encerramento. Nesta página, você
                pode entender melhor essa decisão e acompanhar o andamento da
                sua disputa.
              </div>
            </div>
          </div>

          <div className="dashBody">
            {renderStatusCard()}

            <section className="surfaceLight" aria-label="Detalhes da decisão">
              <div className="sectionHeader">
                <div className="sectionTitle">Detalhes da decisão</div>
              </div>

              <div className="disputeInfoCard">
                {hasReceivedCompensation ? (
                  <>
                    <div className="disputeReasonHighlight">
                      <div className="disputeInfoText">
                        <strong>Motivo do encerramento:</strong> identificamos o
                        recebimento de compensação anterior na modalidade{" "}
                        <strong>{compensationDetails.type}</strong>.
                      </div>
                    </div>

                    <div className="disputeCompensationGrid">
                      <div className="disputeCompensationItem">
                        <div className="disputeCompensationLabel">
                          Modalidade
                        </div>
                        <div className="disputeCompensationValue">
                          {compensationDetails.type}
                        </div>
                      </div>

                      <div className="disputeCompensationItem">
                        <div className="disputeCompensationLabel">
                          Valor recebido
                        </div>
                        <div className="disputeCompensationValue">
                          {formatCurrency(compensationDetails.value)}
                        </div>
                      </div>

                      <div className="disputeCompensationItem">
                        <div className="disputeCompensationLabel">
                          Data do acordo
                        </div>
                        <div className="disputeCompensationValue">
                          {formatDate(compensationDetails.agreementDate)}
                        </div>
                      </div>

                      <div className="disputeCompensationItem">
                        <div className="disputeCompensationLabel">
                          Data do pagamento
                        </div>
                        <div className="disputeCompensationValue">
                          {formatDate(compensationDetails.paymentDate)}
                        </div>
                      </div>
                    </div>

                    <div className="disputeInfoText">
                      Este registro é o motivo pelo qual sua ação está em
                      processo de encerramento.
                    </div>

                    <div className="disputeInfoText">
                      Se você não recebeu essa compensação, ou deseja contestar
                      essa decisão por outro motivo, pode prosseguir com a
                      disputa abaixo para análise da nossa equipe.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="disputeInfoText">
                      Não identificamos registro de compensação anterior no seu
                      cadastro.
                    </div>

                    <div className="disputeInfoText">
                      Se você não concorda com a decisão de encerramento, pode
                      seguir com a disputa para que a nossa equipe revise o seu
                      caso.
                    </div>
                  </>
                )}
              </div>
            </section>

            {renderWhatHappensSection()}
            {renderBeforeContinueSection()}
            {renderFaqSection()}

            <div className="disputeBackRow">
              <button
                type="button"
                className="docsBackBtn"
                onClick={() => navigate("/dashboard")}
              >
                Voltar
              </button>
            </div>

            <div className="bottomSpacer" />
          </div>
        </div>
      </div>
    </>
  );
}
